import React, { useEffect, useState } from 'react'
import NewAccModal from './NewAccModal';
import ToastMessage from '../../../common/ToastMessage';
import CustomDataGrid from '../../../common/CustomDataGrid';
import useUsers from '../../../store/ReactQueries/users/useUser';
import useAddUsers from '../../../store/ReactQueries/users/useAddUsers';
import useUpdateUser from '../../../store/ReactQueries/users/useUpdateUser';
import useUpdateUserWithPassForget from '../../../store/ReactQueries/usersWithPassword/useUpdateUserWithPassForgStore';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../utils/constants';
import { EndPoints } from '../../../utils/apiEndpoints';
import { FieldErrors } from '../../../domain/helpers/Errors';
import { validateSchema } from '../../../common/ValidateSchema';
import { generateNewPassword } from '../../Auth/GeneratePassword';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { capitalizeFirstLetter } from '../../../common/GlobalFunctions';
import { UserInterface } from '../../../models/UserModel/User';
import { userSchema } from '../../../domain/ValidationSchemas/auth/user';

export const Users = () => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [modalTest, setModalTest] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<UserInterface>({
        id: '',
        firstname: '',
        lastname: '',
        email: '',
        isActive: true,
        canAddTickets: false,
    });
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: users, isLoading, error: priorityError } = useUsers(page, pageSize);

    const addUser = useAddUsers(page, pageSize);
    const updateUser = useUpdateUser(page, pageSize);
    const updateUserPassword = useUpdateUserWithPassForget(page, pageSize);

    useEffect(() => {
        if (priorityError) ToastMessage.Failed(`Error: ${priorityError.message}`);
    }, [priorityError]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        {
            field: 'firstname', headerName: 'Firstname', type: "string", editable: false, width: 150,
            renderCell: ({ row }) => <div className='w-100'>
                <span>{capitalizeFirstLetter(row.firstname)}</span>
            </div>,
        },
        {
            field: 'lastname', headerName: 'Lastname', type: "string", editable: false, width: 150,
            renderCell: ({ row }) => <div className='w-100'>
                <span>{capitalizeFirstLetter(row.lastname)}</span>
            </div>,
        },
        { field: 'email', headerName: 'Email', type: "string", editable: false, width: 250 },
        { field: 'roles', headerName: 'Roles', type: "string", editable: false, width: 250 },
        {
            field: "isActive",
            headerName: "Active",
            type: "boolean",
            editable: false,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? "white" : "black" }}>
                    {value ? `✓` : "✕"}
                </span>
            ),
        },
        {
            field: "canAddTickets",
            headerName: "Can Add Tickets",
            type: "boolean",
            editable: false,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? "white" : "black" }}>
                    {value ? `✓` : "✕"}
                </span>
            ),
        },
    ];

    const addItems = () => {
        setForm({ ...form, firstname: '', lastname: '', email: '', isActive: true, canAddTickets: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleSave = async () => {
        const isValid = validateSchema(userSchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: UserInterface = {
            id: form.id,
            email: form.email,
            isActive: form.isActive,
            lastname: form.lastname,
            firstname: form.firstname,
            canAddTickets: form.canAddTickets,
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addUser.mutate(formData, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => ToastMessage.Failed(error.message),
            });
        } else {
            updateUser.mutate(upForm, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => ToastMessage.Failed(error.message),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleReset = async (row: UserInterface) => {
        if (await ToastMessage.Confirm(language['ConfirmChangePassword'])) {
            const newPassword = generateNewPassword();
            setPassword(newPassword);
            setForm(row);

            const upForm: { email: string, password: string } = {
                email: row.email,
                password: newPassword
            };

            updateUserPassword.mutate(upForm, {
                onSuccess: () => {
                    setModalTest(true);
                    ToastMessage.Success()
                },
                onError: (error) => ToastMessage.Failed(error.message)
            });
        }
    }

    return (
        <React.Fragment>
            <CustomDataGrid
                name={language['Users']}
                rows={users || []}
                form={form}
                page={page}
                modal={modal}
                errors={errors}
                columns={columns}
                pageSize={pageSize}
                loading={isLoading}
                isResetPassword={false}
                setForm={setForm}
                setPage={setPage}
                addItems={addItems}
                setModal={setModal}
                setErrors={setErrors}
                handleSave={handleSave}
                setPageSize={setPageSize}
                handleReset={handleReset}
                newAddComponent='Users'
                showColumnModal={['title']}
                hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
            />
            {
                modalTest
                && <NewAccModal
                    open={modalTest}
                    email={form.email}
                    password={password}
                    route={EndPoints.USERS}
                    setOpen={setModalTest}
                />
            }
        </React.Fragment>
    );
}
export default Users;